/**
 * EquityInjectionWizard
 *
 * Guided one-step dialog that creates a balanced pair of journal entries:
 *   1. balance / equity_contributions / +$X   (cash injection onto the Balance Sheet)
 *   2. income  / other_opex           / +$X   (matching operating expense, optional)
 *
 * Both entries use the same period and notes so they're traceable together.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button }   from '@/components/ui/button';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { http }  from '@/lib/api';
import { toast }    from 'sonner';
import { ArrowRightLeft, CheckCircle2, Loader2 } from 'lucide-react';

const CURRENT_YEAR = new Date().getUTCFullYear();
const PERIOD_OPTIONS = [
  `${CURRENT_YEAR}-YTD`,
  ...Array.from({ length: 12 }, (_, i) =>
    `${CURRENT_YEAR}-${String(i + 1).padStart(2, '0')}`),
  `${CURRENT_YEAR - 1}-YTD`,
];

const OPEX_CATEGORIES = [
  { value: 'other_opex',    label: 'Other Operating Expenses' },
  { value: 'platform_dev',  label: 'Platform Development' },
  { value: 'api_costs',     label: 'API Vendor Costs' },
  { value: 'advertising',   label: 'Advertising' },
  { value: 'rent',          label: 'Rent' },
  { value: 'office',        label: 'Office & Supplies' },
  { value: 'salaries',      label: 'Salaries' },
];

export default function EquityInjectionWizard({ open, onClose, onPosted }) {
  const [form, setForm] = useState({
    purpose:         '',
    amount:          '',
    period:          `${CURRENT_YEAR}-YTD`,
    withOpex:        true,
    expenseCategory: 'other_opex',
  });
  const [busy,    setBusy]    = useState(false);
  const [posted,  setPosted]  = useState(false);

  const amount = parseFloat(form.amount) || 0;
  const valid  = form.purpose.trim().length >= 3 && amount > 0;

  function reset() {
    setForm({ purpose: '', amount: '', period: `${CURRENT_YEAR}-YTD`, withOpex: true, expenseCategory: 'other_opex' });
    setPosted(false);
  }

  async function handleSubmit() {
    if (!valid) return;
    setBusy(true);
    try {
      const base = {
        period: form.period,
        notes:  form.purpose.trim(),
      };
      // 1. Balance-sheet equity injection
      await http.post('/admin/accounting/journal', {
        ...base,
        statement: 'balance',
        category:  'equity_contributions',
        amount_usd: amount,
      });
      // 2. Matching opex entry (optional)
      if (form.withOpex) {
        await http.post('/admin/accounting/journal', {
          ...base,
          statement: 'income',
          category:  form.expenseCategory || 'other_opex',
          amount_usd: amount,
        });
      }
      setPosted(true);
      toast.success('Equity injection recorded successfully.');
      onPosted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to post entries.');
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md" data-testid="equity-wizard-dialog">
        {posted ? (
          /* ── Success state ── */
          <div className="flex flex-col items-center gap-4 py-6 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500" />
            <DialogTitle>Injection Posted</DialogTitle>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {form.withOpex
                ? 'Both the equity contribution and the operating expense have been recorded. The Balance Sheet, Income Statement, and Cash Flow Statement will reflect the change on next load.'
                : 'The equity contribution has been recorded. The Balance Sheet will reflect the change on next load.'}
            </p>
            <Button
              data-testid="equity-wizard-done-btn"
              onClick={handleClose}
              className="mt-2 w-full"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-primary" />
                Record Equity Injection
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Creates a balanced pair of journal entries — an equity
                contribution on the Balance Sheet and an optional matching
                operating expense on the Income Statement.
              </p>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Purpose */}
              <div className="space-y-1.5">
                <Label htmlFor="eq-purpose">Purpose</Label>
                <Input
                  id="eq-purpose"
                  data-testid="equity-wizard-purpose-input"
                  placeholder="e.g. Emergent Development — Q4 platform build"
                  value={form.purpose}
                  onChange={(e) => setForm(f => ({ ...f, purpose: e.target.value }))}
                />
              </div>

              {/* Amount */}
              <div className="space-y-1.5">
                <Label htmlFor="eq-amount">Amount (USD)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                  <Input
                    id="eq-amount"
                    data-testid="equity-wizard-amount-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="0.00"
                    className="pl-7"
                    value={form.amount}
                    onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                  />
                </div>
              </div>

              {/* Period */}
              <div className="space-y-1.5">
                <Label htmlFor="eq-period">Accounting Period</Label>
                <select
                  id="eq-period"
                  data-testid="equity-wizard-period-select"
                  value={form.period}
                  onChange={(e) => setForm(f => ({ ...f, period: e.target.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {PERIOD_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              {/* OpEx toggle */}
              <label
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 hover:bg-muted/40"
                data-testid="equity-wizard-opex-toggle"
              >
                <input
                  type="checkbox"
                  checked={form.withOpex}
                  onChange={(e) => setForm(f => ({ ...f, withOpex: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span className="text-sm leading-snug">
                  <span className="font-medium">Also record as operating expense</span>
                  <br />
                  <span className="text-muted-foreground text-xs">
                    Net cash effect = $0. Income Statement will show the spend;
                    the balance sheet stays balanced.
                  </span>
                </span>
              </label>

              {/* Expense category — only shown when opex is on */}
              {form.withOpex && (
                <div className="space-y-1.5">
                  <Label htmlFor="eq-expense-cat">Expense Category</Label>
                  <select
                    id="eq-expense-cat"
                    data-testid="equity-wizard-expense-cat"
                    value={form.expenseCategory}
                    onChange={(e) => setForm(f => ({ ...f, expenseCategory: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {OPEX_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Preview */}
              {valid && (
                <div
                  data-testid="equity-wizard-preview"
                  className="rounded-md border border-dashed border-border bg-muted/30 p-3 space-y-2 text-xs font-mono"
                >
                  <p className="text-muted-foreground uppercase tracking-wide text-[10px] mb-1">
                    Entry preview
                  </p>
                  <div className="flex justify-between">
                    <span className="text-emerald-600 dark:text-emerald-400">
                      BALANCE / equity_contributions
                    </span>
                    <span>+${amount.toFixed(2)}</span>
                  </div>
                  {form.withOpex && (
                    <div className="flex justify-between">
                      <span className="text-amber-600 dark:text-amber-400">
                        INCOME / {form.expenseCategory || 'other_opex'}
                      </span>
                      <span>+${amount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="border-t border-border/50 mt-1 pt-1 flex justify-between text-muted-foreground">
                    <span>Net cash effect</span>
                    <span className={form.withOpex ? '' : 'text-emerald-600 dark:text-emerald-400'}>
                      {form.withOpex ? '$0.00' : `+$${amount.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={handleClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                data-testid="equity-wizard-submit-btn"
                onClick={handleSubmit}
                disabled={!valid || busy}
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Post Entries
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
